'use client';

import { createSvgActiveContentCheck, svgActiveContentMessage } from '@/collections/_validations/svg-safety-rules';
import { declaredUploadViolation, dimensionViolation, SVG_MIME_TYPE } from '@/collections/_validations/upload-rules';
import { ExpectedError } from '@/utils/error-reporting';
import createDOMPurify from 'dompurify';

import { resolveFileMimeType } from './resolve-mime-in-browser';

const assertNoViolation = (violation: null | string): void => {
  if (violation) throw new ExpectedError(violation);
};

const assertWithinDimensionLimit = async (file: File, mimeType: string): Promise<void> => {
  if (!mimeType.startsWith('image/') || mimeType === SVG_MIME_TYPE) return;

  let bitmap: ImageBitmap;

  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return;
  }

  const { height, width } = bitmap;

  bitmap.close();

  assertNoViolation(dimensionViolation(file.name, width, height));
};

const assertSafeSvgFile = async (file: File, mimeType: string): Promise<void> => {
  if (mimeType !== SVG_MIME_TYPE) return;

  const carriesActiveContent = createSvgActiveContentCheck(createDOMPurify(window));

  if (carriesActiveContent(await file.text())) {
    throw new ExpectedError(svgActiveContentMessage(file.name));
  }
};

/**
 * Runs the upload rules in the browser, and reports the type the bytes really are.
 *
 * Every check reads that resolved type rather than `File.type`, which the browser fills from the
 * extension. An SVG named `.gif` is scanned as the SVG it is, and the caller asks for a signature
 * over the formats that match its bytes, so Cloudinary is never sent a file its signature forbids.
 */
export const validateBeforeUpload = async (file: File): Promise<string> => {
  const mimeType = await resolveFileMimeType(file);

  assertNoViolation(declaredUploadViolation(file.name, file.size, mimeType));
  await assertWithinDimensionLimit(file, mimeType);
  await assertSafeSvgFile(file, mimeType);

  return mimeType;
};
